/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import type { HostContext, Logger } from '../@types';
const uuid = require('uuid/v1');
import getConfig from '../config';
import { SET_SEARCH_GLOBAL_CONFIG } from '../actionTypes';
import defaultHostConfigJson from '../config/defaultHostConfig.json';
import { getErrorMessage } from '../utils/getErrorMessage';
import { getLogger } from '../utils/SearchLogger';
import WebStorage from '../storage/WebStorage';
import SearchConfig from '../utils/SearchConfig';
import PerformanceUtils from '../utils/PerformanceUtils';
import Request from '../utils/Request';

class ConfigService {
	private static instance: ConfigService | undefined;
	private readonly logger: Logger;
	private readonly searchHostContext: HostContext;
	private readonly _store: Record<string, any>;
	private _isAlreadyFetching = false;
	private configPromise: Promise<Record<string, any>> | undefined;

	constructor(searchHostContext: HostContext, store: Record<string, any>) {
		if (!ConfigService.instance) {
			this.logger = getLogger();
			this._store = store;
			this.searchHostContext = searchHostContext;
			ConfigService.instance = this;
		}

		return ConfigService.instance;
	}

	/**
	 * Refetches the config if local storage's configuration timestamp is older than twenty-four hours.
	 */
	async refetchConfig(forceFetch = false): Promise<void> {
		const localStorageConfigWithTimestamp = WebStorage.localStorage.getItem('searchConfigWithTimestamp') as string;

		if (localStorageConfigWithTimestamp) {
			const { timestamp, config: localStorageConfig } = JSON.parse(localStorageConfigWithTimestamp);
			const currentTime = new Date();
			const twentyFourHoursInMilliseconds = 24 * 60 * 60 * 1000;

			const time24HoursAgo = new Date(currentTime.getTime() - twentyFourHoursInMilliseconds);

			this.logger.log('Config Service', 'Timestamp of Local Storage Config:', timestamp);
			this.logger.log('Config Service', 'Timestamp of Time 24 Hours Ago:', time24HoursAgo);

			const isValidLocalStorageConfig = this._validateConfig(localStorageConfig);

			if (forceFetch || new Date(timestamp).getTime() < time24HoursAgo.getTime()) {
				this.logger.log('Config Service', 'Past 24 hours. Re-fetching search config.');
				this._isAlreadyFetching = false;
				await this.fetchConfig();
			} else if (isValidLocalStorageConfig) {
				this.logger.log(
					'Config Service',
					'Config still valid with proper properties for the past 24 hours. Doing nothing.',
				);
			} else {
				this.logger.error(
					'Config Service',
					'Config from Local Storage failed to validate. Setting DEFAULT config.',
				);
				this._setDefaultConfig();
			}
		} else {
			this.logger.log('Config Service', 'No config in local storage on refetch.');

			// If there isn't a config in local storage, let's refetch the config.
			await this.fetchConfig();
		}
	}

	private _setConfigInLocalStorageWithTimestamp(config: Record<string, any>): void {
		this.logger.log(
			'Config Service',
			`Setting a valid config in local storage. Config keys: ${Object.keys(config).toString()}`,
		);
		const timestampConfig = {
			config,
			timestamp: new Date().toISOString(),
		};
		WebStorage.localStorage.setItem('searchConfigWithTimestamp', JSON.stringify(timestampConfig));
	}

	async fetchConfig(): Promise<void> {
		try {
			if (!this._isAlreadyFetching) {
				this.configPromise = this.performConfigRequest();
			}
			const response = (await this.configPromise) || {};

			this.logger.log('Config Service', 'Fetch Config SUCCESS from server');
			const validConfig = this._validateConfig(response);

			if (validConfig) {
				this._setConfigInLocalStorageWithTimestamp(response);
				this._dispatchSetConfig(response);
			} else {
				this.logger.error('Config Service', 'Config from server failed to validate. Setting DEFAULT config.');
				this._setDefaultConfig();
			}

			this.logger.log(
				'Config Service',
				`Set config for: [${this.searchHostContext.hostInfo.surface},
					   ${this.searchHostContext.hostInfo.client}].`,
			);
		} catch (e) {
			this.logger.error('Config Service', 'Fetch Config FAILED. Setting DEFAULT Config.', getErrorMessage(e));
			this._setDefaultConfig();
		}
	}

	private _validateConfig(config: Record<string, any>): boolean {
		this.logger.log('Config Service', 'Validating config');
		if (config && typeof config === 'object' && Object.keys(config).length) {
			this.logger.log('Config Service', `Validating config with keys: ${Object.keys(config).toString()}`);

			if (!config.search) {
				this.logger.error('Config Service', 'Config INVALID. No Search body found.');
				return false;
			}

			if (!config.ui) {
				this.logger.error('Config Service', 'Config INVALID. No UI body found.');
				return false;
			}

			return true;
		}

		return false;
	}

	private _dispatchSetConfig(config: Record<string, any>): void {
		this.logger.log('Config Service', `Putting config in store with keys: ${Object.keys(config).toString()}`);

		try {
			// Also add it to the global static class for non-React components
			SearchConfig.setSearchConfig(config);

			this._store.dispatch({
				type: SET_SEARCH_GLOBAL_CONFIG,
				payload: config,
			});
		} catch (err: any) {
			this.logger.error('Config Service: _dispatchSetConfig', err.message);
		}
	}

	private _setDefaultConfig(): void {
		const defaultConfig = this._getDefaultConfig();

		if (Object.keys(defaultConfig).length > 0) {
			this.logger.log(
				'Config Service',
				`Default config being set with keys: ${Object.keys(defaultConfig).toString()}`,
			);

			this._setConfigInLocalStorageWithTimestamp(defaultConfig);
			this._dispatchSetConfig(defaultConfig);
		} else if (this.searchHostContext.environment !== 'prod') {
			// If defaultConfig is an empty object, throw an error in non-prod environments
			throw new Error('UNABLE to set DEFAULT Config. Invalid Client/Surface.');
		}
	}

	private _getDefaultConfig(): Record<string, any> {
		const defaultHostConfigData = defaultHostConfigJson;
		let retVal: Record<string, any> = {};

		if (defaultHostConfigData.surfaces) {
			const surface = defaultHostConfigData.surfaces[this.searchHostContext.hostInfo.surface];

			if (surface) {
				const client = surface[this.searchHostContext.hostInfo.client];

				if (client) {
					if (client.default) {
						retVal = client.default;
					}
				}
			}
		}

		return retVal;
	}

	private async performConfigRequest(): Promise<Promise<Record<string, any>> | Error> {
		this._isAlreadyFetching = true;
		const startTime = Date.now();
		const curRequestId = uuid();
		const hostInfo = this.searchHostContext.hostInfo;
		const clientVersion = `${hostInfo.version.major}.${hostInfo.version.minor}.${hostInfo.version.revision}`;

		try {
			const hostname = getConfig(this.searchHostContext.environment, 'CONFIG_SERVER');
			const path = `/v1/surfaces/${hostInfo.surface}/clients/${hostInfo.client}/configurations/${clientVersion}`;

			const headers = {
				authorization: `Bearer ${this.searchHostContext.imsInfo.accessToken}`,
				'x-api-key': this.searchHostContext.hostInfo.apiKey,
				'x-request-id': curRequestId,
			};

			const configResponse = await Request.withTimeout(5000)
				.withHeaders(headers)
				.send('GET', `https://${hostname}${path}`);
			this._isAlreadyFetching = false;

			if (configResponse.isError()) {
				this.logger.error('Config Service', 'Problem with Search Config response in Search Config Service', {
					responseUrl: configResponse.requestUrl(),
					responseStatusCode: configResponse.status(),
					responseStatusMessage: configResponse.statusText(),
					responseTime: Date.now() - startTime,
					performanceTiming: PerformanceUtils.getPerformanceTimingByName(configResponse.requestUrl()),
				});
				const searchServiceError = new Error(`Config Service response error code: ${configResponse.status()}`);
				return searchServiceError;
			}

			return configResponse.data();
		} catch (err) {
			this._isAlreadyFetching = false;
			this.logger.error('Config Service', 'Problem with fetching Search Config in Search Config Service.', {
				message: getErrorMessage(err),
				requestId: curRequestId,
			});
			const searchError = new Error('_performSearchConfigRequest error');
			return searchError;
		}
	}
}

export default ConfigService;
